import React, { useEffect, useState } from "react"
import {
  Col,
  Row,
  UncontrolledAlert,
  Spinner,
} from "reactstrap"
import api from "API/Api"
import {
  AvForm,
  AvField,
  AvRadioGroup,
  AvRadio,
} from "availity-reactstrap-validation"
import { Link, useParams } from "react-router-dom"

// Helper: Yes/No radio group
// Note: AvRadioGroup treats "0" as falsy so required would block submission.
// All fields have defaults so required is not needed for validation.
const YN = ({ name, label, value, req }) => (
  <Col md={6}>
    <div className="mb-3">
      <AvRadioGroup inline name={name} value={value || "0"} label={label + (req ? " *" : "")} errorMessage="Pick one!">
        <AvRadio label="Yes" value="1" />
        <AvRadio label="No" value="0" />
      </AvRadioGroup>
    </div>
  </Col>
)

// Section heading
const SH = ({ icon, title }) => (
  <h5 className="mb-3 mt-4" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px', color: '#495057' }}>
    <i className={`fas ${icon} me-2`}></i>{title}
  </h5>
)

const AddBill = props => {
  const { id } = useParams()
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [bill, setBill] = useState([])

  useEffect(() => {
    getData()
  }, [])

  const getData = async () => {
    try {
      const resp = await api.get("bill/" + id)
      if (resp.data.success === true && resp.data.data && resp.data.data.length > 0) {
        setBill(resp.data.data[0])
      }
    } catch (error) {
      console.error("Failed to fetch bill data:", error)
    }
  }

  const handleValidSubmit = async (event, values) => {
    setLoading(true)
    try {
      if (bill.company > 0) {
        const response = await api.put("update-bill/" + bill.id, values)
        if (response.data.success === true) setSubmitted(true)
      } else {
        values.company = id
        const response = await api.post("create-bill", values)
        if (response.data.success === true) setSubmitted(true)
      }
    } catch (error) {
      // Handled by global interceptor
    }
    setLoading(false)
  }

  const b = bill // shorthand

  return (
    <>
      <div className="dashBody">
        <div className="font-size-18 mb-0 dashHeader">
          <Row>
            <Col md={6}>
              <h4>Edit Bill Customization</h4>
            </Col>
            <Col md={6} style={{ textAlign: "right" }}>
              <Link to="/bill-customization" className="dashBtn">
                <i className=" fas fa-chevron-left "></i> <b> Back</b>
              </Link>
            </Col>
          </Row>
        </div>
        <div className="dashContent">
          <AvForm
            style={{ position: "relative" }}
            className="form-horizontal mt-4"
            onValidSubmit={(e, v) => {
              handleValidSubmit(e, v)
            }}
          >
            <AvField name="company" value={id} type="hidden" required />

            {/* ==================== SECTION 1: Bill Customization Information ==================== */}
            <SH icon="fa-file-alt" title="Bill Customization Information" />
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <AvField
                    name="billMainHeading"
                    label="Bill Print Main Heading *"
                    value={b.billMainHeading}
                    className="form-control"
                    type="text"
                    required
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <AvField
                    name="billSubHeading"
                    label="Bill Print Sub Heading"
                    value={b.billSubHeading}
                    type="textarea"
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <AvField
                    name="producerVendorBillPrint"
                    label="Producer and Vendor Bill Print"
                    value={b.producerVendorBillPrint || ""}
                    type="text"
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <YN name="advanceDetails" label="Print Advance Details in Bill" value={b.advanceDetails} req={false} />
              <YN name="loanDetails" label="Print Loan Details in Bill" value={b.loanDetails} req={false} />
            </Row>
            <Row>
              <YN name="recoveryDetails" label="Print Recovery Details in Bill" value={b.recoveryDetails} req={false} />
              <YN name="printAdditionDetails" label="Print Addition Details in Bill" value={b.printAdditionDetails} req={true} />
            </Row>
            <Row>
              <YN name="deductionMaterials" label="Print Deduction Materials in Bill" value={b.deductionMaterials} req={true} />
              <YN name="accountDetailsVendorAgent" label="Print Account Details in Vendor/Agent Bill" value={b.accountDetailsVendorAgent} req={true} />
            </Row>
            <Row>
              <YN name="accountDetailsProducer" label="Print Account Details in Producer Bill" value={b.accountDetailsProducer} req={true} />
              <Col md={6}>
                <div className="mb-3">
                  <AvRadioGroup inline name="paperSize" value={b.paperSize || "A4"} label="Print Paper Size *" errorMessage="Pick one!">
                    <AvRadio label="A4" value="A4" />
                    <AvRadio label="A5" value="A5" />
                  </AvRadioGroup>
                </div>
              </Col>
            </Row>

            {/* ==================== SECTION 2: Display & Measurement ==================== */}
            <SH icon="fa-flask" title="Display & Measurement Settings" />
            <Row>
              <YN name="displayPercentFatSnf" label="Display % FAT & % SNF" value={b.displayPercentFatSnf || "1"} req={true} />
              <YN name="displayKgFatSnf" label="Display KG FAT & KG SNF" value={b.displayKgFatSnf} req={true} />
            </Row>
            <Row>
              <YN name="displayLiterFatSnf" label="Display Liter FAT & Liter SNF" value={b.displayLiterFatSnf} req={true} />
              <YN name="showMilkType" label="Print Milk Type" value={b.showMilkType || "1"} req={false} />
            </Row>
            <Row>
              <YN name="printCLR" label="Print CLR" value={b.printCLR} req={true} />
              <YN name="waterPercent" label="Print Water Percent" value={b.waterPercent} req={true} />
            </Row>
            <Row>
              <YN name="printRateWithIncentive" label="Print Rate with Incentive" value={b.printRateWithIncentive} req={true} />
              <YN name="printAvgClrWater" label="Print Average FAT, SNF, CLR and Water Percentage in Total Row" value={b.printAvgClrWater} req={false} />
            </Row>
            <Row>
              <YN name="printAvgRateIncentive" label="Print Average Rate and Incentive in Total Row" value={b.printAvgRateIncentive} req={false} />
            </Row>

            {/* ==================== SECTION 3: Decimal Precision ==================== */}
            <SH icon="fa-sort-numeric-down" title="Decimal Precision" />
            <Row>
              <Col md={3}>
                <div className="mb-3">
                  <AvRadioGroup inline name="fatDecimal" value={b.fatDecimal || "2"} label="FAT Decimal Places *" errorMessage="Pick one!">
                    <AvRadio label="0" value="0" />
                    <AvRadio label="1" value="1" />
                    <AvRadio label="2" value="2" />
                  </AvRadioGroup>
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <AvRadioGroup inline name="snfDecimal" value={b.snfDecimal || "2"} label="SNF Decimal Places *" errorMessage="Pick one!">
                    <AvRadio label="0" value="0" />
                    <AvRadio label="1" value="1" />
                    <AvRadio label="2" value="2" />
                  </AvRadioGroup>
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <AvRadioGroup inline name="quantityDecimal" value={b.quantityDecimal || "2"} label="Quantity Decimals *" errorMessage="Pick one!">
                    <AvRadio label="0" value="0" />
                    <AvRadio label="1" value="1" />
                    <AvRadio label="2" value="2" />
                  </AvRadioGroup>
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <AvRadioGroup inline name="rateDecimal" value={b.rateDecimal || "2"} label="Rate Decimals" errorMessage="Pick one!">
                    <AvRadio label="0" value="0" />
                    <AvRadio label="1" value="1" />
                    <AvRadio label="2" value="2" />
                  </AvRadioGroup>
                </div>
              </Col>
            </Row>

            {/* ==================== SECTION 4: Bill Layout & Entries ==================== */}
            <SH icon="fa-th-list" title="Bill Layout & Entry Options" />
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <AvRadioGroup inline name="orderEntriesBy" value={b.orderEntriesBy || "1"} label="Order Entries by *" errorMessage="Pick one!">
                    <AvRadio label="Date & Shift" value="1" />
                    <AvRadio label="Milk Type" value="2" />
                    <AvRadio label="Shift & Date" value="3" />
                  </AvRadioGroup>
                </div>
              </Col>
              <YN name="showAllVlccEntries" label="Show All Purchase Entries Individually in VLCC Bill Printout" value={b.showAllVlccEntries} req={true} />
            </Row>
            <Row>
              <YN name="showAllCcEntries" label="Show All Purchase Entries Individually in CC Bill Printout" value={b.showAllCcEntries} req={true} />
              <YN name="displayEmptyRow" label="Display Empty Row in Bill in case of Producer/Vendor Absent for the Shift" value={b.displayEmptyRow} req={true} />
            </Row>
            <Row>
              <YN name="printBillNumber" label="Print Bill Number" value={b.printBillNumber || "1"} req={true} />
            </Row>

            {/* ==================== SECTION 5: Financial Details ==================== */}
            <SH icon="fa-rupee-sign" title="Financial Details in Bill" />
            <Row>
              <YN name="printIncentiveDetails" label="Print Incentive Details" value={b.printIncentiveDetails} req={false} />
              <YN name="printBonusDetails" label="Print Quality/Volume Bonus Details" value={b.printBonusDetails} req={false} />
            </Row>
            <Row>
              <YN name="printTransportCharges" label="Print Transport Charges" value={b.printTransportCharges} req={false} />
              <YN name="printRate" label="Print Rate in Bill" value={b.printRate || "1"} req={false} />
            </Row>

            {/* ==================== SECTION 6: Account Details ==================== */}
            <SH icon="fa-university" title="Layout & Branding" />
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <AvRadioGroup inline name="billCopies" value={b.billCopies || "1"} label="Number of Bill Copies" errorMessage="Pick one!">
                    <AvRadio label="1" value="1" />
                    <AvRadio label="2" value="2" />
                    <AvRadio label="3" value="3" />
                  </AvRadioGroup>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <AvRadioGroup inline name="printCompanyLogo" value={b.printCompanyLogo || "1"} label="Print Company Logo" errorMessage="Pick one!">
                    <AvRadio label="Yes" value="1" />
                    <AvRadio label="No" value="0" />
                  </AvRadioGroup>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="mb-3">
                  <AvField
                    name="billFooterText"
                    label="Bill Footer Text / Notes"
                    value={b.billFooterText || ""}
                    type="textarea"
                    placeholder="Enter custom footer text"
                    rows="3"
                  />
                </div>
              </Col>
            </Row>

            {/* ==================== SECTION 7: CC Summary Bill Print ==================== */}
            <SH icon="fa-building" title="CC Summary Bill Print" />
            <Row>
              <YN name="ccVendorPaymentMode" label="Print Vendor Payment Mode in CC Summary" value={b.ccVendorPaymentMode} req={true} />
              <YN name="ccIncentiveTotal" label="Print Incentive Total Amount in CC Summary" value={b.ccIncentiveTotal} req={true} />
            </Row>
            <Row>
              <YN name="ccAdvanceTotal" label="Print Advance Total Amount in CC Summary" value={b.ccAdvanceTotal} req={true} />
              <YN name="ccAdvanceOpening" label="Print Advance Opening in CC Summary" value={b.ccAdvanceOpening} req={true} />
            </Row>
            <Row>
              <YN name="ccAdvanceDeduction" label="Print Advance Deduction in CC Summary" value={b.ccAdvanceDeduction} req={true} />
              <YN name="ccAdvanceClosing" label="Print Advance Closing in CC Summary" value={b.ccAdvanceClosing} req={true} />
            </Row>
            <Row>
              <YN name="ccLoanTotal" label="Print Loan Total Amount in CC Summary" value={b.ccLoanTotal} req={true} />
              <YN name="ccLoanOpening" label="Print Loan Opening in CC Summary" value={b.ccLoanOpening} req={true} />
            </Row>
            <Row>
              <YN name="ccLoanDeduction" label="Print Loan Deduction in CC Summary" value={b.ccLoanDeduction} req={true} />
              <YN name="ccLoanClosing" label="Print Loan Closing in CC Summary" value={b.ccLoanClosing} req={true} />
            </Row>
            <Row>
              <YN name="ccMaterialTotal" label="Print Material Total Amount in CC Summary" value={b.ccMaterialTotal} req={true} />
              <YN name="ccMaterialOpening" label="Print Material Opening in CC Summary" value={b.ccMaterialOpening} req={true} />
            </Row>
            <Row>
              <YN name="ccMaterialDeduction" label="Print Material Deduction in CC Summary" value={b.ccMaterialDeduction} req={true} />
              <YN name="ccMaterialClosing" label="Print Material Closing in CC Summary" value={b.ccMaterialClosing} req={true} />
            </Row>
            <Row>
              <YN name="ccRecoveryTotal" label="Print Recovery Total Amount in CC Summary" value={b.ccRecoveryTotal} req={true} />
              <YN name="ccRecoveryOpening" label="Print Recovery Opening in CC Summary" value={b.ccRecoveryOpening} req={true} />
            </Row>
            <Row>
              <YN name="ccRecoveryDeduction" label="Print Recovery Deduction in CC Summary" value={b.ccRecoveryDeduction} req={true} />
              <YN name="ccRecoveryClosing" label="Print Recovery Closing in CC Summary" value={b.ccRecoveryClosing} req={true} />
            </Row>
            <Row>
              <YN name="ccFundDeduction" label="Print Fund Deduction Amount in CC Summary" value={b.ccFundDeduction} req={true} />
              <YN name="ccAdditionsPayout" label="Print Additions Payout in CC Summary" value={b.ccAdditionsPayout} req={true} />
            </Row>

            {/* ==================== SECTION 8: VLCC Summary Bill Print ==================== */}
            <SH icon="fa-warehouse" title="VLCC Summary Bill Print" />
            <Row>
              <YN name="vlccProducerPaymentMode" label="Print Producer Payment Mode in VLCC Summary" value={b.vlccProducerPaymentMode} req={true} />
              <YN name="vlccIncentiveTotal" label="Print Incentive Total Amount in VLCC Summary" value={b.vlccIncentiveTotal} req={true} />
            </Row>
            <Row>
              <YN name="vlccAdvanceTotal" label="Print Advance Total Amount in VLCC Summary" value={b.vlccAdvanceTotal} req={true} />
              <YN name="vlccAdvanceOpening" label="Print Advance Opening in VLCC Summary" value={b.vlccAdvanceOpening} req={true} />
            </Row>
            <Row>
              <YN name="vlccAdvanceDeduction" label="Print Advance Deduction in VLCC Summary" value={b.vlccAdvanceDeduction} req={true} />
              <YN name="vlccAdvanceClosing" label="Print Advance Closing in VLCC Summary" value={b.vlccAdvanceClosing} req={true} />
            </Row>
            <Row>
              <YN name="vlccLoanTotal" label="Print Loan Total Amount in VLCC Summary" value={b.vlccLoanTotal} req={true} />
              <YN name="vlccLoanOpening" label="Print Loan Opening in VLCC Summary" value={b.vlccLoanOpening} req={true} />
            </Row>
            <Row>
              <YN name="vlccLoanDeduction" label="Print Loan Deduction in VLCC Summary" value={b.vlccLoanDeduction} req={true} />
              <YN name="vlccLoanClosing" label="Print Loan Closing in VLCC Summary" value={b.vlccLoanClosing} req={true} />
            </Row>
            <Row>
              <YN name="vlccMaterialTotal" label="Print Material Total Amount in VLCC Summary" value={b.vlccMaterialTotal} req={true} />
              <YN name="vlccMaterialOpening" label="Print Material Opening in VLCC Summary" value={b.vlccMaterialOpening} req={true} />
            </Row>
            <Row>
              <YN name="vlccMaterialDeduction" label="Print Material Deduction in VLCC Summary" value={b.vlccMaterialDeduction} req={true} />
              <YN name="vlccMaterialClosing" label="Print Material Closing in VLCC Summary" value={b.vlccMaterialClosing} req={true} />
            </Row>
            <Row>
              <YN name="vlccRecoveryTotal" label="Print Recovery Total Amount in VLCC Summary" value={b.vlccRecoveryTotal} req={true} />
              <YN name="vlccRecoveryOpening" label="Print Recovery Opening in VLCC Summary" value={b.vlccRecoveryOpening} req={true} />
            </Row>
            <Row>
              <YN name="vlccRecoveryDeduction" label="Print Recovery Deduction in VLCC Summary" value={b.vlccRecoveryDeduction} req={true} />
              <YN name="vlccRecoveryClosing" label="Print Recovery Closing in VLCC Summary" value={b.vlccRecoveryClosing} req={true} />
            </Row>
            <Row>
              <YN name="vlccFundDeduction" label="Print Fund Deduction Amount in VLCC Summary" value={b.vlccFundDeduction} req={true} />
            </Row>

            {/* ==================== SECTION 9: Agent Commission Print ==================== */}
            <SH icon="fa-user-tie" title="Agent Commission Print" />
            <Row>
              <YN name="agentPrintFat" label="Print FAT Value in Agent Commission" value={b.agentPrintFat} req={true} />
              <YN name="agentPrintSnf" label="Print SNF Value in Agent Commission" value={b.agentPrintSnf} req={true} />
            </Row>

            {/* ==================== SECTION 10: Payment Summary Download ==================== */}
            <SH icon="fa-download" title="Payment Summary Download" />
            <Row>
              <YN name="roundOffCcPayment" label="Round Off Total Amount in CC Payment Summary Download" value={b.roundOffCcPayment} req={true} />
              <YN name="roundOffVlccPayment" label="Round Off Total Amount in VLCC Payment Summary Download" value={b.roundOffVlccPayment} req={true} />
            </Row>

            {/* ==================== SECTION 11: Bill Generation Restrictions ==================== */}
            <SH icon="fa-lock" title="Bill Generation Restrictions" />
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <AvField type="select" name="ccBillGenLevel" label="CC Procurement Bill Generation Level" value={b.ccBillGenLevel || "any"}>
                    <option value="any">Any</option>
                    <option value="company">Company</option>
                    <option value="plant">Plant</option>
                    <option value="cc">Chilling Center</option>
                  </AvField>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <AvField type="select" name="vlccBillGenLevel" label="VLCC Procurement Bill Generation Level" value={b.vlccBillGenLevel || "any"}>
                    <option value="any">Any</option>
                    <option value="company">Company</option>
                    <option value="plant">Plant</option>
                    <option value="vlcc">VLCC</option>
                  </AvField>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <AvField type="select" name="ccCommissionBillGenLevel" label="CC Commission Bill Generation Level" value={b.ccCommissionBillGenLevel || "any"}>
                    <option value="any">Any</option>
                    <option value="company">Company</option>
                    <option value="plant">Plant</option>
                    <option value="cc">Chilling Center</option>
                  </AvField>
                </div>
              </Col>
            </Row>

            {/* ==================== Submit ==================== */}
            <div className="mb-3 row mt-4">
              <div className="col-6">
                {submitted ? (
                  <UncontrolledAlert color="success">
                    <strong>Bill Customization is Updated!</strong>
                  </UncontrolledAlert>
                ) : null}
              </div>
              <div className="col-6 text-end">
                <button
                  className="btn btn-primary w-md waves-effect waves-light"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      <span style={{ paddingLeft: "10px" }}>Saving...</span>
                    </>
                  ) : (
                    <span>{bill.company > 0 ? "Update Bill" : "Save Bill"}</span>
                  )}
                </button>
              </div>
            </div>
          </AvForm>
        </div>
      </div>
    </>
  )
}

export default AddBill
